import { useIonRouter } from '@ionic/vue';
import { useRouter } from 'vue-router';

/**
 * Makes tab buttons always navigate to their root page (their href).
 *
 * This overrides two built-in Ionic behaviors:
 * - resetTab(): tapping the active tab on a child page only jumps back in
 *   the browser history and silently does nothing when the tab root was
 *   never visited in this session (e.g. when landing directly on a detail
 *   page via a homepage teaser).
 * - changeTab(): switching to another tab restores the last visited child
 *   page of that tab instead of its root, which users found confusing.
 */
export function useTabRootNavigation() {
  const router = useRouter();
  const ionRouter = useIonRouter();

  const handleTabBarClick = (event: Event) => {
    const button = (event.target as HTMLElement | null)?.closest(
      'ion-tab-button',
    ) as (HTMLElement & { href?: string }) | null;
    const href = button?.href || button?.getAttribute('href');
    if (!href) {
      return;
    }

    const currentPath = router.currentRoute.value.path;
    if (currentPath === href) {
      // Already on the root of the tapped tab: nothing to do,
      // Ionic's own handler is a no-op here as well.
      return;
    }

    // Intercept before Ionic's own click handler (resetTab/changeTab) runs,
    // otherwise both would navigate.
    event.preventDefault();
    event.stopPropagation();

    // Going up within the same tab gets a back animation,
    // switching tabs none (matches Ionic's default for tab changes).
    const direction = currentPath.startsWith(`${href}/`) ? 'back' : 'none';
    ionRouter.navigate(href, direction, 'push');
  };

  return { handleTabBarClick };
}
